import { useMemo, useRef } from "react";
import styled, { css } from "styled-components/macro";
import StoreDisplay from "./StoreDisplay";
import { filterObjectByRemovingKeys } from "utils/arrayUtils";
import { animated } from "@react-spring/web";
import {
  ControlsAnchor,
  DEBUGGER_HEADER_HEIGHT,
  DEBUGGER_MEDIA_QUERY,
  DEBUGGER_PADDING,
  useBindDragHandler,
  useDebuggerPopupState,
} from "./debuggerUtils";
import { useEventListener } from "utils/customHooks/genericHooks";
import useWindowSize from "utils/customHooks/useWindowSize";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import { getIsTouchDevice } from "utils/getIsTouchDevice";
import { PopupHeader } from "./PopupHeader";
import { CUSTOM_SCROLLBAR_CSS } from "App/CUSTOM_SCROLLBAR_CSS";
import { darkModeCardBackgroundColor } from "constants/colors";
import { useIsDarkMode } from "store/atoms";
import { CustomJsonEditor } from "./debuggerComponents/CustomJsonEditor";

const POPUP_WIDTH = 500;

const PopupData = () => {
  const [{ popupData, isPreview, collapsed }, setDebuggerPopupState] =
    useDebuggerPopupState();
  /** for touch devices, dragging doesn't work, so we'll pin it to the bottom and expand all the way up on click */
  const isTouchDevice = getIsTouchDevice();

  const [isDarkMode] = useIsDarkMode();

  const windowSize = useWindowSize();
  const width = useMemo(
    () => Math.min(windowSize.innerWidth, POPUP_WIDTH),
    [windowSize.innerWidth]
  );
  const { style, bindDragHandler } = useBindDragHandler({
    anchor: ControlsAnchor.TOP_RIGHT,
    width,
    localStorageKey: "flight-debugger:popup:position",
  });

  const handleClosePopup = () => {
    setDebuggerPopupState(prev => ({
      ...prev,
      popupData: null,
      isPreview: false,
      isPopupOpen: false,
    }));
  };

  // close on Esc keydown
  useEventListener("keydown", e => {
    if (["Escape"].includes(e.key)) {
      handleClosePopup();
    }
  });
  const { innerHeight } = useWindowSize();
  const iframeRef = useRef(null as any);

  if (!popupData) {
    return null;
  }
  const { iframeUrl, data, networkName, CustomComponent, viewStore, popupId } =
    popupData;
  if (iframeUrl && iframeRef.current) {
    populateIframe(iframeRef.current, iframeUrl, [
      [
        "Content-Security-Policy",
        "frame-ancestors https://flight.raven.ai https://fledge.raven.ai http://localhost:3000",
      ],
    ]);
  }

  return (
    <AnimatedPopupStyles
      className={`${isPreview ? " preview" : ""}${
        viewStore ? " consoleData" : ""
      }`}
      style={{
        width,
        ...style,
        ...(collapsed ? { height: "fit-content" } : {}),
      }}
      $isTouchDevice={isTouchDevice}
      $isDarkMode={isDarkMode}
      $innerWidth={innerWidth}
    >
      <PopupHeader
        {...{
          isTouchDevice,
          bindDragHandler,
          networkName,
        }}
      />
      <animated.div
        hidden={collapsed}
        style={{
          maxHeight: isTouchDevice
            ? `calc(100vh - ${DEBUGGER_HEADER_HEIGHT}px)`
            : undefined,
        }}
        className={`networkPopupContents${
          !CustomComponent && !iframeUrl && popupId !== "flightConfigEditor"
            ? " customJsonEditor"
            : ""
        }`}
        onClick={e =>
          // only allow background click to close popup
          e.stopPropagation()
        }
      >
        <div className={`dataWrapper popup-id-${popupId}`}>
          {CustomComponent ? (
            <CustomComponent />
          ) : iframeUrl ? (
            <ErrorBoundary id={`PopupData_${networkName}`}>
              <iframe
                ref={iframeRef}
                {...(networkName ? { title: networkName } : {})}
                src={iframeUrl}
                sandbox={""} // prevent JavaScript execution from within the iframe https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe#attr-sandbox
              />
            </ErrorBoundary>
          ) : data ? (
            <CustomJsonEditor
              key={JSON.stringify(data)}
              /* timeAxisContext contains a DOM node (can't stringify circular structures) */
              value={filterObjectByRemovingKeys(data, ["timeAxisContext"])}
              height={innerHeight - 64}
            />
          ) : null}
          {viewStore ? <StoreDisplay /> : null}
        </div>
      </animated.div>
    </AnimatedPopupStyles>
  );
};

export default PopupData;

const AnimatedPopupStyles = styled(animated.div)<{
  $isDarkMode: boolean;
  $isTouchDevice: boolean;
  $innerWidth: number;
}>`
  box-shadow: 0px 4px 10px 2px #0000004f;
  border-radius: 8px;
  z-index: 999999999;
  opacity: 0.95;
  position: fixed;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  h1.title {
    font-size: 1.5em;
    padding-bottom: 1em;
  }
  .dragArea {
    width: 100%;
    height: 100%;
    display: grid;
    place-items: center left;
  }
  .networkPopupContents {
    ${CUSTOM_SCROLLBAR_CSS}
    height: 100%;
    width: 100%;
    box-sizing: border-box;
    pointer-events: auto;
    box-shadow: 0px 5px 17px -1px #00000042;
    border-bottom-right-radius: 8px;
    border-bottom-left-radius: 8px;
    padding: 2em;
    &.customJsonEditor {
      padding: 0;
    }
    max-width: 600px;
    &.consoleData {
      max-width: 1000px;
    }
    background: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeCardBackgroundColor : "white"};
    position: relative;
    overflow: auto;

    .btnClose {
      position: absolute;
      top: 0;
      right: 0;
    }
    .dataWrapper {
      word-wrap: break-word;
      height: 100%;
      &.popup-id-flightConfigEditor {
        height: 100%;
      }
    }
    pre,
    .consoleDataWrapper {
      background: hsl(0, 0%, 93%);
      user-select: text;
      cursor: text;
    }
  }
  &.preview {
    opacity: 0.5;
    pointer-events: none;
    * {
      pointer-events: none;
    }
    .btnClose {
      pointer-events: auto;
    }
  }
  .object-key {
    user-select: all;
  }
  /* for touch devices, attach to & pop up from the bottom */
  ${props =>
    props.$isTouchDevice && props.$innerWidth < 600
      ? css`
          transform: translateY(-${DEBUGGER_PADDING.y / 2}px) !important;
          flex-direction: column-reverse;
          top: auto;
          bottom: -${DEBUGGER_PADDING.y / 2}px;
          left: 0;
        `
      : ""}
  /* for small mobile devices, stretch to full width */
  ${DEBUGGER_MEDIA_QUERY} {
    right: 0;
    width: auto;
  }
  ${CUSTOM_SCROLLBAR_CSS}
`;

function populateIframe(iframe, url, headers) {
  const xhr = new XMLHttpRequest();
  xhr.open("GET", url);
  xhr.onreadystatechange = handler;
  xhr.responseType = "blob";
  headers.forEach(function (header) {
    xhr.setRequestHeader(header[0], header[1]);
  });
  xhr.send();

  function handler(this) {
    if (this.readyState === this.DONE) {
      if (this.status === 200) {
        iframe.src = URL.createObjectURL(this.response);
      } else {
        console.error("Request failed", this);
      }
    }
  }
}
